import React, { useState, useMemo } from 'react';

const FolderPickerModal = ({ folders, onSelect, onClose, title = "Select Folder", allowRoot = false }) => {
  const [search, setSearch] = useState('');
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const folderMap = useMemo(() => new Map(folders.map(f => [f._id, f])), [folders]);

  // Build breadcrumbs for current folder
  const breadcrumbs = useMemo(() => {
    const crumbs = [];
    let curr = currentFolderId ? folderMap.get(currentFolderId) : null;
    while (curr) {
      crumbs.unshift(curr);
      curr = curr.parent ? folderMap.get(curr.parent.toString()) : null;
    }
    return crumbs;
  }, [currentFolderId, folderMap]);

  // Get folders to display
  const displayedFolders = useMemo(() => {
    if (search) {
      return folders.filter(f => f.title.toLowerCase().includes(search.toLowerCase()));
    }
    return folders.filter(f => {
      const parentId = f.parent ? f.parent.toString() : null;
      return parentId === currentFolderId;
    }).sort((a, b) => a.title.localeCompare(b.title));
  }, [folders, search, currentFolderId]);

  const handleBack = () => {
    if (!currentFolderId) return;
    const curr = folderMap.get(currentFolderId);
    setCurrentFolderId(curr?.parent?.toString() || null);
    setSelectedId(null);
  };

  const handleSelect = () => {
    const idToSelect = selectedId !== null ? selectedId : (currentFolderId || '');
    if (!allowRoot && idToSelect === '') return;
    onSelect(idToSelect);
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 11000 }}>
      <div className="modal-card" onClick={e => e.stopPropagation()} style={{ width: 'min(600px, 100%)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
        
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', background: '#f8fafc' }}>
          <h3 style={{ margin: 0, fontSize: '0.95rem', color: '#334155', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</h3>
          <button type="button" onClick={onClose} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#94a3b8', padding: '4px' }}>✕</button>
        </div>

        {/* Search */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
          <input 
            type="text" 
            placeholder="Search folders..." 
            value={search}
            onChange={e => { setSearch(e.target.value); setSelectedId(null); }}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
          />
        </div>

        {/* Path Bar / Breadcrumbs */}
        {!search && (
          <div style={{ padding: '8px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button 
              type="button"
              onClick={handleBack}
              disabled={!currentFolderId}
              style={{ 
                background: currentFolderId ? '#fff' : 'transparent', 
                border: currentFolderId ? '1px solid #cbd5e1' : '1px solid transparent', 
                borderRadius: '4px', width: '28px', height: '28px', 
                display: 'flex', alignItems: 'center', justifyContent: 'center', 
                cursor: currentFolderId ? 'pointer' : 'default', 
                color: currentFolderId ? '#0f172a' : '#cbd5e1' 
              }}
              title="Up one level"
            >
              <i className="fas fa-arrow-up" style={{ fontSize: '0.85rem' }} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflowX: 'auto', whiteSpace: 'nowrap', flex: 1, padding: '4px 10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.9rem' }}>
              <span 
                onClick={() => { setCurrentFolderId(null); setSelectedId(null); }} 
                style={{ cursor: 'pointer', color: currentFolderId ? '#3b82f6' : '#0f172a', fontWeight: currentFolderId ? 'normal' : '600' }}
              >
                Root
              </span>
              {breadcrumbs.map(crumb => (
                <React.Fragment key={crumb._id}>
                  <span style={{ color: '#cbd5e1' }}>/</span>
                  <span 
                    onClick={() => { setCurrentFolderId(crumb._id); setSelectedId(null); }} 
                    style={{ cursor: 'pointer', color: currentFolderId === crumb._id ? '#0f172a' : '#3b82f6', fontWeight: currentFolderId === crumb._id ? '600' : 'normal' }}
                  >
                    {crumb.title}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Folders List (Compact View) */}
        <div style={{ flex: 1, overflowY: 'auto', background: '#fff' }} onClick={() => setSelectedId(null)}>
          {search && displayedFolders.length === 0 && <div style={{ color: '#94a3b8', textAlign: 'center', padding: '40px 20px', fontSize: '0.95rem' }}>No folders match your search.</div>}
          {!search && displayedFolders.length === 0 && <div style={{ color: '#94a3b8', textAlign: 'center', padding: '40px 20px', fontSize: '0.95rem' }}>This folder is empty.</div>}

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {allowRoot && !currentFolderId && !search && (
              <div 
                style={{ 
                  cursor: 'pointer', padding: '10px 16px', 
                  display: 'flex', alignItems: 'center', gap: '12px',
                  borderBottom: '1px solid #f1f5f9',
                  background: selectedId === '' ? '#eff6ff' : 'transparent',
                  transition: 'background 0.1s'
                }}
                onClick={(e) => { e.stopPropagation(); setSelectedId(''); }}
                onMouseEnter={e => {
                  if (selectedId !== '') e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={e => {
                  if (selectedId !== '') e.currentTarget.style.background = 'transparent';
                }}
              >
                <i className="fas fa-folder" style={{ color: '#94a3b8', fontSize: '1.2rem' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.95rem', color: selectedId === '' ? '#1e3a8a' : '#0f172a', fontWeight: selectedId === '' ? '600' : '500' }}>Root Folder</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>System Directory</div>
                </div>
              </div>
            )}
            {displayedFolders.map(f => {
              const isSelected = selectedId === f._id;
              return (
                <div 
                  key={f._id}
                  style={{ 
                    cursor: 'pointer', padding: '10px 16px', 
                    display: 'flex', alignItems: 'center', gap: '12px',
                    borderBottom: '1px solid #f1f5f9',
                    background: isSelected ? '#eff6ff' : 'transparent',
                    transition: 'background 0.1s'
                  }}
                  onClick={(e) => { e.stopPropagation(); setSelectedId(f._id); }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    setCurrentFolderId(f._id);
                    setSelectedId(null);
                    setSearch('');
                  }}
                  onMouseEnter={e => {
                    if (!isSelected) e.currentTarget.style.background = '#f8fafc';
                  }}
                  onMouseLeave={e => {
                    if (!isSelected) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <i className="fas fa-folder" style={{ color: '#fbbf24', fontSize: '1.2rem' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.95rem', color: isSelected ? '#1e3a8a' : '#0f172a', fontWeight: isSelected ? '600' : '500' }}>{f.title}</div>
                    {f.college && f.college !== f.title && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{f.college}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Bar */}
        <div style={{ padding: '20px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button 
            type="button"
            className="secondary"
            onClick={() => onSelect(currentFolderId || '')}
            disabled={!allowRoot && !currentFolderId}
            style={{ 
              padding: '10px 18px',
              fontSize: '0.9rem',
              fontWeight: '500',
              whiteSpace: 'nowrap'
            }}
          >
            Use Current Folder
          </button>
          
          <button 
            type="button"
            className="primary"
            onClick={() => onSelect(selectedId)}
            disabled={selectedId === null}
            style={{ 
              padding: '10px 22px',
              fontSize: '0.9rem',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}
          >
            Select Selected
          </button>
        </div>

      </div>
    </div>
  );
};

export default FolderPickerModal;
